import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { environment } from '@env/environment';
import { CustomerHttpParamsCodec } from '@helpers/custom-http-params-codec';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { Page } from '@interfaces/page';
import { RvsCategory } from '@models/rvs-category.model';
import { prepareDateForDb } from '@app/_utils/date-util';

import { IcashCatalogItem } from '../_models/icash-catalog-item.model';
import { ListSale } from '../_models/list-sale.model';
import { Sale } from '../_models/sale.model';

const salesUrl       = environment.apiBaseURL + '/sale';
const catalogItemsUrl = salesUrl + '/catalog/items';
const carCategoryUrl  = salesUrl + '/car-category';
const findByIdUrl     = salesUrl + '/{id}';
const annulUrl        = salesUrl + '/{id}/annul';
const pageableUrl      = salesUrl + '/pageable';
const pageableCountUrl = salesUrl + '/pageable-count';

@Injectable({
  providedIn: 'root'
})
export class SaleService {

  constructor(private http: HttpClient) { }

  /** Creates the sale with its lines and the order that pays for it; returns the new id. */
  public save(sale: Sale): Observable<number> {
    return this.http.post<number>(salesUrl, sale);
  }

  /**
   * Records an edit as a new version of the sale.
   *
   * The id that comes back is the new version's, not the one passed in — the old row stays as
   * history with its versionData no longer actual.
   */
  public update(id: number, sale: Sale): Observable<number> {
    return this.http.put<number>(findByIdUrl.replace('{id}', `${id}`), sale);
  }

  /**
   * Voids the sale, recording the annulment as a new version and annulling its order.
   *
   * Like update(), the id that comes back is the new version's. The sale leaves the list once
   * this returns, so there is nothing to navigate back to but the list itself.
   */
  public annul(id: number): Observable<number> {
    return this.http.put<number>(annulUrl.replace('{id}', `${id}`), {});
  }

  public findById(id: number): Observable<Sale> {
    return this.http.get<Sale>(findByIdUrl.replace('{id}', `${id}`));
  }

  public pageable(sortingPaging: SortingPagingData, searchForm: any):
      Observable<[Page<ListSale>, number]> {
    return forkJoin([
      this.findAllByFilter(sortingPaging, searchForm),
      this.countAllByFilter(searchForm)
    ]);
  }

  public findAllByFilter(sortingPaging: SortingPagingData, searchForm: any): Observable<Page<ListSale>> {
    return this.http.get<Page<ListSale>>(pageableUrl,
        { params: this.buildSearchParams(searchForm, sortingPaging) });
  }

  public countAllByFilter(searchForm: any): Observable<number> {
    return this.http.get<number>(pageableCountUrl, { params: this.buildSearchParams(searchForm, null) });
  }

  /**
   * Each parameter is set by name rather than by looping the form: an empty select posts '' and
   * fails to bind to a number server-side, and a looped `length` test silently drops a 0.
   */
  private buildSearchParams(searchForm: any, sortingPaging: SortingPagingData | null): HttpParams {
    let params = new HttpParams({ encoder: new CustomerHttpParamsCodec() });

    if (sortingPaging != null) {
      params = params.set('page', `${sortingPaging.getPageNumber()}`);
      params = params.set('pageSize', `${sortingPaging.getPageSize()}`);
    }

    if (searchForm?.techPointMobileAppId) {
      params = params.set('mobileAppId', `${searchForm.techPointMobileAppId}`);
    }
    if (searchForm?.regNumber) {
      params = params.set('regNumber', `${searchForm.regNumber}`);
    }

    const dateFrom = prepareDateForDb(searchForm?.dateFrom);
    if (dateFrom) {
      params = params.set('dateFrom', dateFrom);
    }
    const dateTo = prepareDateForDb(searchForm?.dateTo);
    if (dateTo) {
      params = params.set('dateTo', dateTo);
    }

    return params;
  }

  /**
   * One page of the iCash catalogue for the goods picker.
   *
   * There is no store parameter: the backend prices against the iCash store of the technical
   * point, so the screen cannot ask for another counter's prices.
   *
   * Unlike the database-backed screens there is no count call — iCash returns the total with
   * the rows, so Page.totalElements is already populated.
   */
  public findCatalogItems(techPointMobileAppId: number, q: string | null,
                          page: number, pageSize: number): Observable<Page<IcashCatalogItem>> {
    let requestParams = new HttpParams({ encoder: new CustomerHttpParamsCodec() });
    requestParams = requestParams.set('techPointMobileAppId', techPointMobileAppId.toString());
    requestParams = requestParams.set('page', page.toString());
    requestParams = requestParams.set('pageSize', pageSize.toString());

    // Set one by one rather than looping: an empty search box sends '', which the backend would
    // take as a filter matching nothing instead of as "no filter".
    if (q) { requestParams = requestParams.set('q', q); }

    return this.http.get<Page<IcashCatalogItem>>(catalogItemsUrl, { params: requestParams });
  }

  /**
   * One article with its stock read live from iCash, for the check run when an item is added.
   *
   * Separate from the catalogue search because it reaches through to the till system for the
   * current stock — worth one call when committing to sell something, too expensive per row of
   * a search box.
   */
  public findLiveCatalogItem(techPointMobileAppId: number, saleItemId: number): Observable<IcashCatalogItem> {
    let requestParams = new HttpParams();
    requestParams = requestParams.set('techPointMobileAppId', techPointMobileAppId.toString());

    return this.http.get<IcashCatalogItem>(`${catalogItemsUrl}/${saleItemId}`, { params: requestParams });
  }

  /**
   * The vehicle category for a registration number.
   *
   * Answers with an empty body when the plate resolves to nothing — an unknown vehicle is a
   * normal outcome at a counter, so the caller leaves the chosen category alone.
   */
  public findCategoryByRegNumber(registrationNumber: string): Observable<RvsCategory> {
    let requestParams = new HttpParams({ encoder: new CustomerHttpParamsCodec() });
    requestParams = requestParams.set('registrationNumber', registrationNumber);

    return this.http.get<RvsCategory>(carCategoryUrl, { params: requestParams }).pipe(shareReplay());
  }
}
